'use strict';
const Loader = require('common/infra/loader');
const Logger = require('common/util/logger');
const Snackbar = require('common/util/snackbar');
const _ = require('underscore');
const i18n = require('i18next');
const $ = require('jquery');
const moment = require('moment');
const Util = require('common/util');
const DebugContextRepository = require('common/util/debug-context/debug-context-repository');
const buildInfo = require('buildinfo');

/**
 * エラーの通知用文言を取得する。
 *
 * error.json に書かれた文言が取得される。
 * @example
 * var errorDoc = Balus.getErrorContent('InternalServerError');
 *
 * // errorDoc.title: ...
 * // errorDoc.content: ...
 *
 * Balus.notify.error(errorDoc);
 *
 * @param {String|Number} errorType
 * @returns {Object}
 */
const getErrorContent = function(errorType) {
    const msg = i18n.t(
        'error:error.' + errorType,
        { returnObjects: true, defaultValue: '' }
    );
    if (msg && msg != '') {
        return msg;
    }
    if (errorType >= 400 && errorType < 500) {
        return getErrorContent(400);
    }
    if (errorType >= 500 && errorType < 600) {
        return getErrorContent(500);
    }
};

const getAjaxErrorType = function(jqXHR, statusText) {
    let errorType;
    if(statusText == 'timeout') {
        errorType = 'AjaxTimeoutError';
    }

    if(statusText == 'abort') {
        errorType = 'AjaxAbortError';
    }

    if(statusText == 'parsererror') {
        errorType = 'AjaxParseError';
    }

    if(statusText == 'error') {
        try {
            const json = $.parseJSON(jqXHR.responseText);
            if('type' in json) {
                errorType = json.type;
            } else {
                errorType = jqXHR.status;
            }
        } catch(e) {
            errorType = 'InternalServerError'
        }
    } else {
        errorType = jqXHR.status;
    }
    return errorType;
};

class Headers {
    constructor(headers) {
        let h = {};
        _.each(headers, (v, k) => {
            h[k.toLowerCase()] = v;
        });
        this._headers = h;
    }

    get(name) {
        return this._headers[name.toLowerCase()];
    }

    has(name) {
        return _.has(this._headers, name.toLowerCase());
    }
}

const parseHTTPHeaders = function(headerString) {
    let headers = {};
    _.each(headerString.split('\r\n'), nv => {
        if(nv.trim() == '') {
            return;
        }

        let name = nv.split(':', 1);
        if(name.length != 1) {
            console.error(`Invalid Header: ${nv}`);
        }
        name = name[0];
        let value = nv.slice(name.length + 1).trim();
        name = name.trim();

        headers[name] = value;
    });

    return new Headers(headers);
};


const ajax = function(method, url, body, options) {
    const requestId = Util.generateId();
    const debugContext = (new DebugContextRepository()).fetch();
    const additionalQueryParams = [];

    // デバッグコンテキストでリバースプロキシが無効になっている場合、クエリパラメータ rp=off を付与する
    if (debugContext.getOptions().reverseProxyIsDisabled()) {
        additionalQueryParams.push('rp=off');
    }

    // デバッグコンテキストでリバースプロキシ先のバージョンが指定されている場合、クエリパラメータで指定する
    if (debugContext.getTNGVersion()) {
        additionalQueryParams.push(`target_tng_version=${debugContext.getTNGVersion()}`);
    }

    if (additionalQueryParams.length > 0) {
        url += (url.indexOf('?') >= 0 ? '&' : '?') + additionalQueryParams.join("&");
    }

    let settings = {
        type: method,
        url: url,
        contentType: 'application/json',
        dataType: 'json',
        headers: {
            'X-Client-Request-Id': requestId,
            'X-Client-Debug-Token': debugContext.getToken(),
            'X-Client-Git-SHA1': buildInfo.git_sha1,
        }
    };

    if(body) {
        settings.data = JSON.stringify(body);
    }

    if(options && options.extraHeaders) {
        settings.headers = _.extend(settings.headers, options.extraHeaders);
    }

    const ajaxStartedAt = moment();
    const ajaxLogger = (requestId, ajaxStartedAt, state, currentTime) => {
        const logPrefix = `ajax(requestId=${requestId}, startedAt=${ajaxStartedAt.toJSON()})`;
        const dt = currentTime.diff(ajaxStartedAt);
        let logMessage = `${logPrefix} t=${currentTime.toJSON()}, state=${state}, dt=${dt}ms`;
        Logger.debug(logMessage);
    };
    Logger.debug(`ajax(requestId=${requestId}) settings=${JSON.stringify(settings)}`);

    Loader.start();
    return new Promise(function(resolve, reject) {
        ajaxLogger(requestId, ajaxStartedAt, 'start', moment());
        $.ajax(settings)
            .done((data, textStatus, jqXHR) => {
                ajaxLogger(requestId, ajaxStartedAt, 'done', moment());
                if(options && options.withResponseHeaders) {
                    resolve({
                        data: data,
                        headers: parseHTTPHeaders(jqXHR.getAllResponseHeaders())
                    });
                } else {
                    resolve(data);
                }
            })
            .fail((jqXHR, textStatus, errorThrown) => {
                ajaxLogger(requestId, ajaxStartedAt, 'fail', moment());
                if(options && options.noRaise) {
                    resolve();
                } else {
                    const type = getAjaxErrorType(jqXHR, textStatus);
                    if(options && options.ignoreErrorTypes && _.include(options.ignoreErrorTypes, type)) {
                        resolve();
                        return;
                    }

                    const status = jqXHR.status;
                    const errorMessage = getErrorContent(type);
                    if(status !== 401 && errorMessage && errorMessage != '') {
                        Snackbar.error(errorMessage);
                    }
                    Logger.error(`ajax error: ${type}, ${textStatus}`);

                    reject({
                        status: status,
                        type: type,
                        jqXHR: jqXHR
                    });
                }
            })
            .always(function () {
                Loader.end();

                ajaxLogger(requestId, ajaxStartedAt, 'finished', moment());
            });
    });
};

module.exports = {
    GET: function(url, options) {
        return ajax('GET', url, null, options);
    },

    POST: function(url, body) {
        return ajax('POST', url, body);
    },

    PUT: function(url, body) {
        return ajax('PUT', url, body);
    },

    DELETE: function(url) {
        return ajax('DELETE', url);
    },

    getErrorContent: getErrorContent,
    getAjaxErrorType: getAjaxErrorType
};



// WEBPACK FOOTER //
// ./common/infra/ajax.js